import React from "react";
import {
    Checkbox,
    FormControl,
    FormHelperText,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent
} from "@mui/material";
import {PowerStationWithPermission} from "../UsersReactQuery";

const ALL_OPTION = "Todos";

interface UserPowerStationsSelectProps {
    options: PowerStationWithPermission[];
    selectedIds: number[];
    lockedIds: number[];
    disabled: boolean;
    loading: boolean;
    onChange: (ids: number[]) => void;
    helperText?: string;
}

export const UserPowerStationsSelect = ({
    options,
    selectedIds,
    lockedIds,
    disabled,
    loading,
    onChange,
    helperText
}: UserPowerStationsSelectProps) => {
    const allSelected = options.length > 0 && options.every(option => selectedIds.includes(option.id));

    const handleChange = (event: SelectChangeEvent<Array<number | string>>) => {
        const value = event.target.value;
        const values = typeof value === "string" ? value.split(",") : value;

        if (values.includes(ALL_OPTION)) {
            // Without loaded options, "Todos" would send an empty selection and revoke every access.
            if (loading || options.length === 0) {
                return;
            }
            // Locked power stations stay selected even when "Todos" clears the selection.
            onChange(allSelected ? [...lockedIds] : options.map(option => option.id));
            return;
        }
        const ids = values.map(Number);
        onChange([...ids, ...lockedIds.filter(id => !ids.includes(id))]);
    };

    const renderValue = (selected: Array<number | string>) =>
        options
            .filter(option => selected.includes(option.id))
            .map(option => option.name)
            .join(", ");

    return (
        <FormControl className="permissoes" sx={{marginLeft: 1}}>
            <InputLabel id="settings-users-power-stations-label">
                Usinas
            </InputLabel>
            <Select
                labelId="settings-users-power-stations-label"
                id="settings-users-power-stations-select"
                multiple
                disabled={disabled}
                value={selectedIds}
                onChange={handleChange}
                input={<OutlinedInput label="Usinas"/>}
                renderValue={renderValue}
                variant="standard"
                disableUnderline
                sx={{
                    '.MuiOutlinedInput-notchedOutline': {border: 0}
                }}
            >
                <MenuItem value={ALL_OPTION}>
                    <Checkbox checked={allSelected}/>
                    <ListItemText primary="Todos"/>
                </MenuItem>
                {loading && (
                    <MenuItem disabled>
                        <ListItemText primary="Carregando..."/>
                    </MenuItem>
                )}
                {options.map(option => {
                    const locked = lockedIds.includes(option.id);
                    return (
                        <MenuItem key={option.id} value={option.id} disabled={locked}>
                            <Checkbox checked={selectedIds.includes(option.id)}/>
                            <ListItemText primary={option.name}/>
                        </MenuItem>
                    );
                })}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};
